import { useState } from 'react'
import { Button, FormGroup, Modal, ModalBody, ModalFooter } from 'reactstrap'
import { EntityCreator } from '../../data/entity-creator'
import { useInputValue } from '../../hooks/useInputValue'
import { Actor, Spell } from '../../redux/models'
import { useAppDispatch } from '../../redux/redux-hooks'
import { ModalsActions } from '../../redux/slices/modals-slice'
import { ViewTimelineActions } from '../../redux/slices/view-timeline-slice'
import { extractOneFromList, jsonClone, nextOrdinal, sortByOrdinal } from '../../util/utils'
import { ClassColorPicker } from '../ClassColorPicker'
import { LabeledInput } from './LabeledInput'

export const EDIT_ACTOR_MODAL = "EDIT_ACTOR_MODAL"

export type EditActorModalProps = {
    actor: Actor
    spells: Spell[]
}

export const EditActorModal = (props: EditActorModalProps): JSX.Element | null => {

    const appDisp = useAppDispatch()
    const [state, setState] = useState(jsonClone(props))
    const [deletedSpells, setDeletedSpells] = useState<string[]>([])

    const nameInputs = useInputValue({
        initialValue: state.actor.name
    })

    const [color, setColor] = useState(state.actor.color ?? '#000')

    const addSpell = () => {

        const ordinal = nextOrdinal(state.spells)

        const spell = EntityCreator.createSpell({
            actorId: state.actor.id,
            name: 'Untitled',
            cooldown: 60,
            duration: 10,
            ordinal,
            ramp: 0
        })

        setState(state => ({
            ...state,
            spells: [
                ...state.spells,
                spell
            ]
        }))
    }

    const cloneSpell = (spellToClone: Spell) => {

        const ordinal = nextOrdinal(state.spells)

        const newSpell = EntityCreator.createSpell({
            actorId: state.actor.id,
            name: spellToClone.name,
            cooldown: spellToClone.cooldown,
            duration: spellToClone.duration,
            ordinal,
            ramp: spellToClone.ramp
        })

        setState(state => ({
            ...state,
            spells: [
                ...state.spells,
                newSpell
            ]
        }))
    }

    const deleteSpell = (spellId: string) => {
        if (state.spells.length <= 1) {
            alert(`Can't delete last spell`)
            return
        }

        setDeletedSpells(state => ([
            ...state,
            spellId
        ]))

        setState(state => ({
            ...state,
            spells: state.spells.filter(s => s.id !== spellId)
        }))
    }

    const hide = () => {
        setState(state => ({
            ...state,
            actor: {
                ...state.actor,
                hidden: !state.actor.hidden
            }
        }))
    }

    const duplicate = () => {
        appDisp(ViewTimelineActions.cloneActor({ actorId: props.actor.id }))
        appDisp(ModalsActions.popModal())
    }

    const deleteActor = () => {
        appDisp(ViewTimelineActions.deleteActor({ actorId: props.actor.id }))
        appDisp(ModalsActions.popModal())
    }

    const setSpellField = ({ fields, spellId }: { spellId: string; fields: Partial<Spell> }) => {

        const { match, restOfList } = extractOneFromList(state.spells, (s) => s.id === spellId)

        setState(state => ({
            ...state,
            spells: [
                ...restOfList,
                {
                    ...match,
                    ...fields
                }
            ]
        }))
    }

    const changeOrdinal = ({ spellId, dir }: { spellId: string; dir: "up" | "down" }) => {
        if (state.spells.length <= 1) {
            return
        }

        const ordinalSorted = sortByOrdinal(state.spells)
        const targetIndex = ordinalSorted.findIndex(spell => spell.id === spellId)
        const targetSpell = ordinalSorted[targetIndex]

        if (dir === "up") {
            if (targetIndex === 0) {
                return
            }

            const swapTarget = ordinalSorted[targetIndex - 1]

            targetSpell.ordinal--
            swapTarget.ordinal++

        } else {
            if (targetIndex === ordinalSorted.length - 1) {
                return
            }

            const swapTarget = ordinalSorted[targetIndex + 1]

            targetSpell.ordinal++
            swapTarget.ordinal--
        }

        setState(state => ({
            ...state,
            spells: ordinalSorted
        }))
    }

    return (
        <Modal
            isOpen={true}
            fade={false}
            size="lg"
            toggle={() => appDisp(ModalsActions.popModal())}
        >

            <ModalBody>
                <div className="d-flex flex-column space-my-2">
                    <div className="d-flex space-mx-2 align-items-end">
                        <FormGroup className="flex-grow-1">
                            <LabeledInput
                                label={"Actor name"}
                                inputProps={{
                                    value: nameInputs.value,
                                    onChange: nameInputs.onChange
                                }}
                            />
                        </FormGroup>
                        <ClassColorPicker
                            color={color ?? '#000'}
                            onColorChanged={(color) => {
                                setColor(color)
                            }}
                        />
                    </div>

                    <hr />

                    {sortByOrdinal(state.spells).map(spell => {
                        return (
                            <div key={spell.id} className="d-flex space-mx-2 align-items-end">
                                <div className="small">
                                    <Button
                                        outline
                                        onClick={() => changeOrdinal({ spellId: spell.id, dir: "up" })}
                                    >
                                        {"▲"}
                                    </Button>
                                </div>
                                <div className="small">
                                    <Button
                                        outline
                                        onClick={() => changeOrdinal({ spellId: spell.id, dir: "down" })}
                                    >
                                        {"▼"}
                                    </Button>
                                </div>

                                <ClassColorPicker
                                    color={spell.color ?? '#fff'}
                                    onColorChanged={(color) => {
                                        setSpellField({
                                            fields: { color },
                                            spellId: spell.id
                                        })
                                    }}
                                />

                                <div className="small">
                                    <Button
                                        outline
                                        onClick={() => cloneSpell(spell)}
                                    >
                                        {"Clone"}
                                    </Button>
                                </div>

                                <FormGroup className="flex-grow-1">
                                    <LabeledInput
                                        label={"Ability name"}
                                        inputProps={{
                                            value: spell.name,
                                            onChange: (ev) => {
                                                setSpellField({
                                                    fields: { name: ev.target.value },
                                                    spellId: spell.id
                                                })
                                            }
                                        }}
                                    />
                                </FormGroup>
                                <FormGroup style={{ maxWidth: 80 }}>
                                    <LabeledInput
                                        label={"Duration"}
                                        inputProps={{
                                            value: spell.duration,
                                            onChange: (ev) => {
                                                setSpellField({
                                                    fields: { duration: parseInt(ev.target.value) },
                                                    spellId: spell.id
                                                })
                                            }
                                        }}
                                    />
                                </FormGroup>
                                <FormGroup style={{ maxWidth: 80 }}>
                                    <LabeledInput
                                        label={"Cooldown"}
                                        inputProps={{
                                            value: spell.cooldown,
                                            onChange: (ev) => {
                                                setSpellField({
                                                    fields: { cooldown: parseInt(ev.target.value) },
                                                    spellId: spell.id
                                                })
                                            }
                                        }}
                                    />
                                </FormGroup>
                                <FormGroup style={{ maxWidth: 80 }}>
                                    <LabeledInput
                                        label={"Ramp"}
                                        inputProps={{
                                            value: spell.ramp,
                                            onChange: (ev) => {
                                                setSpellField({
                                                    fields: { ramp: parseInt(ev.target.value) },
                                                    spellId: spell.id
                                                })
                                            }
                                        }}
                                    />
                                </FormGroup>

                                <div>
                                    <Button
                                        outline
                                        color="danger"
                                        onClick={() => deleteSpell(spell.id)}
                                    >
                                        {"✖"}
                                    </Button>
                                </div>
                            </div>
                        )
                    })}

                </div>
            </ModalBody>

            <ModalFooter className="d-flex justify-content-between">
                <div className="d-flex space-mx-2">
                    <Button onClick={addSpell}>
                        {"Add ability"}
                    </Button>

                    {props.actor.isBoss === false &&
                        <>
                            <Button onClick={deleteActor} color="danger">
                                {"Delete actor"}
                            </Button>
                            <Button onClick={duplicate}>
                                {"Duplicate"}
                            </Button>
                            <Button onClick={hide}>
                                {state.actor.hidden ? 'Show' : 'Hide'}
                            </Button>
                        </>
                    }
                </div>
                <div className="space-mx-2">

                    <Button onClick={() => {
                        appDisp(ViewTimelineActions.updateActor({
                            actorId: state.actor.id,
                            updateFields: {
                                ...state.actor,
                                name: `${nameInputs.value}`,
                                color: color
                            }
                        }))

                        appDisp(ViewTimelineActions.updateSpells({
                            spells: state.spells,
                            deleteSpells: deletedSpells
                        }))

                        appDisp(ModalsActions.popModal())
                    }}>
                        {"Save"}
                    </Button>

                    <Button onClick={() => appDisp(ModalsActions.popModal())}>
                        {"Cancel"}
                    </Button>

                </div>
            </ModalFooter>
        </Modal>
    )
}